/**
 * The last recovery step: the core's resolver, reserved for explicit user
 * actions (Apply and Refind) because it sends the whole document and may run
 * an LLM. It hands the server the anchor the memory holds and the paragraphs
 * the user changed recently as hints. A conflicted answer is ambiguous, an
 * empty paragraph list is not found, and a relocated answer carries the
 * placement the server chose.
 */
import { ON_PARAGRAPH } from "@common/utils/redline/wire/redline-placement";

import { AnchorLog } from "../anchor-log";
import type { DocumentChangeLedger } from "../document-change-ledger";
import type { ReanchorClient } from "../reanchor-client";
import type { RecoveryContext, RecoveryStep } from "./recovery-chain";
import type { RecoveryConfig } from "../recovery-config";
import { RecoveryResult } from "./recovery-result";

export class ServerStep implements RecoveryStep {
    readonly tier = "server";
    readonly triggers = ["apply", "refind"] as const;

    constructor(
        private readonly reanchor: ReanchorClient,
        private readonly ledger: DocumentChangeLedger,
        private readonly config: RecoveryConfig,
    ) {}

    async recover({ body, memory, bookmarkNames, redlineDesiredText }: RecoveryContext): Promise<RecoveryResult> {
        const changedParagraphTexts = (): string[] => {
            const texts: string[] = [];
            /**
             * EXAMPLE
             *   changed ids          = 8
             *   serverCandidateLimit = 3
             *   slice(0, 3)          -> keep indexes 0, 1, 2
             *   remaining 5          -> dropped from server hints
             */
            for (const uniqueLocalId of this.ledger.touchedSince(memory.watchingSince).slice(0, this.config.serverCandidateLimit)) {
                const paragraph = body.paragraphById(uniqueLocalId);
                const paragraphWasFound = paragraph !== null;
                const paragraphHasNonBlankText = (): boolean => {
                    if (!paragraphWasFound) return false;
                    return paragraph.text.trim().length > 0;
                };
                /**
                 * CASES
                 *   - paragraph exists and text has non-blank content -> push paragraph.text
                 *   - paragraph missing                               -> skip
                 *   - text is only whitespace                         -> skip
                 */
                if (paragraphHasNonBlankText()) texts.push(paragraph.text);
            }
            return texts;
        };
        const documentFlatOpc = body.bodyFlatOpc;
        const spanAnchor = memory.toSpanAnchor();
        const recentChangedParagraphTexts = changedParagraphTexts();

        const response = await this.reanchor.resolve({
            documentFlatOpc,
            anchor: spanAnchor,
            redlineDesiredText,
            changedParagraphTexts: recentChangedParagraphTexts,
        });
        AnchorLog.record({ kind: "server", bookmarkNames, response });
        /**
         * WHEN the resolver does not answer
         *   CONSEQUENCE
         *     Return notFound: there is no server anchor decision to apply.
         */
        if (!response) return RecoveryResult.notFound("the server resolver did not answer");
        const explanation = response.resolverExplanation;
        /**
         * WHEN the server reports anchorState = "conflicted"
         *   CONSEQUENCE
         *     Return ambiguous instead of guessing one paragraph, naming the places the
         *     server ranked so the user can be told which ones fit.
         */
        if (response.anchorState === "conflicted") {
            return RecoveryResult.ambiguous(
                explanation ?? "more than one paragraph fits the redline",
                response.llmCandidates.map((candidate) => ({ paragraphIndex: candidate.paragraphIndex, anchorPhrase: candidate.anchorPhrase, reason: candidate.reason })),
            );
        }
        /**
         * WHEN the server returns no paragraph indexes
         *   CONSEQUENCE
         *     Return notFound: the anchor was understood, but no paragraph matches now.
         */
        if (response.paragraphIndexes.length === 0) return RecoveryResult.notFound(explanation);
        const paragraphs = body.paragraphsAt(response.paragraphIndexes);
        /**
         * WHEN response.paragraphIndexes names paragraphs the body read lists no paragraph for
         *   CONSEQUENCE
         *     Return notFound: the live document no longer has a recoverable target.
         */
        if (paragraphs.length === 0) return RecoveryResult.notFound(explanation);
        /**
         * CASES
         *   - response.placement present -> use the server-chosen placement
         *   - response.placement absent  -> use ON_PARAGRAPH
         */
        return RecoveryResult.recovered({ paragraphs, placement: response.placement ?? ON_PARAGRAPH, explanation, checks: null });
    }
}
